import React, { useState } from 'react';
import useFetchData from '../../../../Hooks/useFetchData';
import {
  ExportAgentPerformanceExcel,
  GetAgentPerformance,
} from '../../../../Services';
import { Tables } from '../../../../Components';
import { ButtonBase, Chip, makeStyles } from '@material-ui/core';
import { useDebouncedAction } from '../../../../Hooks/DebouncedAction';
import SearchFields from './components/SearchFields';
import PerformanceDateRangePicker from '../components/PerformanceDateRangePicker';
import ExportExcelButton from '../components/ExportExcelButton';
import { PerformanceInsightsTranslationPath } from '../i18n/PerformanceInsightsLocale';
import AgentPerformanceKPIsCards from './components/AgentPerformanceKPIsCards';
import { stringToCamelCase } from '../../../../Helper';
import { useKeyGenerator } from '../../../../Hooks/keyGenerator';
import { useTranslation } from 'react-i18next';

const styles = makeStyles({
  topPerformer: {
    backgroundColor: '#D1FADF',
    color: '#027A48',
    border: '1px solid #12B76A',
  },
  consistent: {
    backgroundColor: '#FEF9C3',
    color: '#B54708',
    border: '1px solid #FACC15',
  },
  needsImprovement: {
    backgroundColor: '#FEE4E2',
    color: '#B42318',
    border: '1px solid #F04438',
  },
  noDataMessage: {
    padding: '16px',
    textAlign: 'center',
    color: '#888',
    fontSize: '16px',
  },
});

const AgentPerformance = () => {
  const classes = styles();
  const { t } = useTranslation(PerformanceInsightsTranslationPath);
  const [params, setParams] = useState({
    pageSize: 25,
    pageIndex: 1,
    rotationSchemeProperties: [], // Store selected properties
  });
  const { data, loading } = useFetchData(GetAgentPerformance, params);
  const [key, refresh] = useKeyGenerator();
  const setParamsDebounced = useDebouncedAction(setParams);
  const onSearchHandler = (e, field) => {
    if (field === 'propertyName') {
      setParamsDebounced((prev) => ({
        ...prev,
        propertyIds: e.propertyIds,
        rotationSchemeProperties: e.rotationSchemeProperties,
      }));
    } else if (e && e.target) {
      const { value } = e.target;
      setParamsDebounced((prev) => ({ ...prev, [field]: value }));
    } else {
      setParamsDebounced((prev) => ({
        ...prev,
        [field]: e.value,
      }));
    }
  };
  return (
    <div className='p-3'>
      <AgentPerformanceKPIsCards params={params} />
      <ExportExcelButton
        params={params}
        handler={ExportAgentPerformanceExcel}
      />
      <div className='d-flex-column py-3 px-2'>
        <div className='flex gap-1 py-3'>
          <SearchFields
            onSearchHandler={onSearchHandler}
            key={key}
            params={params}
          />
          <div>
            <PerformanceDateRangePicker
              key={key}
              onDateRangeChange={({ startDate, endDate }) =>
                setParams((prev) => ({
                  ...prev,
                  pageIndex: 1,
                  startDateAssignLead: startDate,
                  endDateAssignLead: endDate,
                }))
              }
            />
          </div>
        </div>
        <div className='d-flex-v-center-h-end'>
          <ButtonBase
            onClick={() => {
              setParams({
                pageIndex: 1,
                pageSize: 10,
                rotationSchemeProperties: [],
              });
              refresh();
            }}
            id='onClearedAllFiltersref'
            className='btns theme-solid bg-danger clear-all-btn max-height'
          >
            <span className='mdi mdi-filter-remove m-1' />
            {t(`clear-filters`)}
          </ButtonBase>
        </div>
      </div>
      {data?.result?.length === 0 && !loading ? (
        <div className={classes.noDataMessage}>
         No campaigns found for the selected branch.
        </div>
      ) : (
        <Tables
          headerData={[
            { label: 'agent', input: 'agentName' },
            {
              label: 'branch/team',
              input: 'branchName',
              component: (row) =>
                [row.branchName, row.teamNames].filter(Boolean).join('/ '),
            },
            {
              label: 'performance',
              input: 'performanceTag',
              component: (row) => (
                <Chip
                  className={classes[stringToCamelCase(row.performanceTag)]}
                  label={row.performanceTag}
                />
              ),
            },
            { label: 'total_assigned', input: 'totalAssignedLeads' },
            { label: 'currently_assigned', input: 'currentAssignedLeads' },
            { label: 'qualified', input: 'qualifiedLeads' },
            { label: 'unqualified', input: 'unqualifiedLeads' },
            { label: 'updated', input: 'updatedLeads' },
            { label: 'not_updated', input: 'notUpdatedLeads' },
            { label: 'lost', input: 'lostLeads' },
            { label: 'closed', input: 'closedLeads' },
            { label: 'transacted', input: 'transactedLeads' },
            { label: 'activeLeads', input: 'activeLeads' },
          ]}
          data={data.result || []}
          isLoading={loading}
          totalItems={data.totalCount || 0}
          activePage={params.pageIndex - 1}
          itemsPerPage={params.pageSize}
          pageIndex={params.pageIndex - 1}
          defaultActions={[]}
          parentTranslationPath={PerformanceInsightsTranslationPath}
          onPageIndexChanged={(pageIndex) =>
            setParams({ ...params, pageIndex: pageIndex + 1 })
          }
          onPageSizeChanged={(pageSize) => setParams({ ...params, pageSize })}
        />
      )}
    </div>
  );
};

export default AgentPerformance;
